import { Box, FormControl, MenuItem, Select, Stack } from '@mui/material'
import { observer } from 'mobx-react-lite'
import StoreAchievement from '../StoreAchievement'
import { useTranslate } from 'GlobalStores/Cores/Translator/useTranslate'
import { lowerCase } from 'lodash'
import { useEffect } from 'react'
import { when, reaction, runInAction } from 'mobx'
import styled from '@emotion/styled'
import { ReactComponent as TriangleArrowLeft } from '../assets/TriangleArrowLeft.svg'

const ArrowButton = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    svg {
        opacity: ${props => (props.disabled ? 0.3 : 1)};
        transform: ${props => (props.rotate ? `rotate(${props.rotate}deg)` : 'none')};
    }
`

const StyledSelect = styled(Select)`
    border-radius: 8px;
    .MuiSelect-root {
        height: 50px;
    }
    .MuiSelect-select {
        height: 50px;
        max-height: 50px;
        padding: 8px 16px !important;
        font-family: 'Inter';
        font-size: 16px;
        color: rgba(34, 170, 236, 1);
        font-weight: 400;
        text-align: center;
    }

    .MuiSelect-icon {
        display: none;
    }

    box-shadow: 'none';
    &.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
        border: 1px solid rgba(227, 232, 237, 1);
    }
    &.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
        border: 1px solid rgba(227, 232, 237, 1);
    }

    &.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
        border: 1px solid rgba(227, 232, 237, 1);
    }
`

const RankSelector = observer(() => {
    const translate = useTranslate()

    useEffect(() => {
        // ใช้ when แทน autorun เพราะต้องการให้ทำงานครั้งเดียวแล้วหยุด
        const disposerWhen = when(
            () => StoreAchievement.achievements.length > 0 && StoreAchievement.getSelectedPeriod() !== '',
            () => {
                const defaultRank = StoreAchievement.achievements[StoreAchievement.getSelectedPeriod()]?.defaultRank
                runInAction(() => {
                    StoreAchievement.defaultRank = defaultRank
                    StoreAchievement.selectedRank = defaultRank
                })
            }
        )

        const disposerChangePeriod = reaction(
            () => StoreAchievement.getSelectedPeriod(),
            (n, p) => {
                if (n !== p && StoreAchievement.achievements.length > 0) {
                    const defaultRank = StoreAchievement.achievements[n]?.defaultRank
                    runInAction(() => {
                        StoreAchievement.defaultRank = defaultRank
                        StoreAchievement.selectedRank = defaultRank
                    })
                }
            }
        )

        return () => {
            disposerWhen()
            disposerChangePeriod()
        }
    }, [])

    const handleChange = event => {
        StoreAchievement.setSelectedRank(event.target.value)
    }

    const handlePrevious = () => {
        StoreAchievement.selectPreviousRank()
    }

    const handleNext = () => {
        StoreAchievement.selectNextRank()
    }

    const currentIndex = StoreAchievement.getSelectedRankIndex()
    const isFirstRank = currentIndex <= 0
    const isLastRank = currentIndex >= StoreAchievement.getRanks().length - 1

    return (
        <Stack direction={'row'} gap="12px" justifyContent={'space-between'} alignItems={'center'} position={'relative'} width={'100%'}>
            <Box
                position={'absolute'}
                left={'0'}
                top={'0px'}
                bottom={0}
                width={'50px'}
                height={'50px'}
                zIndex={2}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'flex-start'}
                onClick={!isFirstRank ? handlePrevious : undefined}
                sx={{
                    cursor: !isFirstRank ? 'pointer' : 'default',
                    paddingLeft: '20px'
                }}>
                {/* Left Arrow */}
                <ArrowButton disabled={isFirstRank}>
                    <TriangleArrowLeft />
                </ArrowButton>
            </Box>

            {/* Select Box */}
            <FormControl size="small" sx={{ width: '100%', '& .MuiOutlinedInput-root': { height: '50px' }, zIndex: 1 }}>
                <StyledSelect
                    value={StoreAchievement.getSelectedRank() || ''}
                    onChange={handleChange}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                borderRadius: '8px',
                                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                maxHeight: 240, // ~5 items (48px per item)
                                '& .MuiMenuItem-root': {
                                    fontFamily: 'Inter',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    color: 'rgba(0, 59, 111, 1)',
                                    borderBottom: '1px solid rgba(227, 232, 237, .8)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(204, 222, 239, 0.3)'
                                    },
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(204, 222, 239, 0.5)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(204, 222, 239, 0.7)'
                                        }
                                    },
                                    '&:last-child': {
                                        borderBottom: 'none'
                                    }
                                },
                                marginTop: '10px'
                            }
                        }
                    }}
                    sx={{
                        '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                        }
                    }}>
                    {StoreAchievement.getRanks().map(rank => (
                        <MenuItem key={rank} value={rank}>
                            {translate(lowerCase(rank).replace(/ /g, '') + '_rank')}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </FormControl>

            <Box
                position={'absolute'}
                right={'0'}
                top={'0px'}
                bottom={0}
                width={'50px'}
                height={'50px'}
                zIndex={2}
                display={'flex'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                onClick={!isLastRank ? handleNext : undefined}
                sx={{
                    cursor: !isLastRank ? 'pointer' : 'default',
                    paddingRight: '20px'
                }}>
                {/* Right Arrow */}
                <ArrowButton disabled={isLastRank} rotate={180}>
                    <TriangleArrowLeft />
                </ArrowButton>
            </Box>
        </Stack>
    )
})

export default RankSelector
